import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-[0.98]",
        outline: "border border-brand-navy/20 bg-background text-brand-navy hover:bg-accent/10 hover:border-brand-gold",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-brand-navy hover:bg-brand-navy/5 hover:text-brand-navy",
        link: "text-primary underline-offset-4 hover:underline",
        // Brand variants
        navy: "bg-brand-navy text-brand-navy-foreground hover:bg-brand-navy/90 active:scale-[0.98]",
        gold: "bg-brand-gold text-brand-gold-foreground hover:bg-brand-gold/90 active:scale-[0.98]",
        premium: "bg-premium text-premium-foreground hover:bg-premium/90 active:scale-[0.98]",
        success: "bg-success text-success-foreground hover:bg-success/90 active:scale-[0.98]",
        // Tertiary - text only with underline on hover
        tertiary: "text-brand-navy hover:underline underline-offset-4 bg-transparent",
        // Outline variants
        "outline-navy": "border-2 border-brand-navy text-brand-navy bg-transparent hover:bg-brand-navy hover:text-brand-navy-foreground",
        "outline-gold": "border-2 border-brand-gold text-brand-gold bg-transparent hover:bg-brand-gold hover:text-brand-gold-foreground",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-sm",
        lg: "h-12 rounded-lg px-8 text-base font-semibold",
        xl: "h-14 rounded-lg px-10 text-lg font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
